import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  clearUserData,
  decodeAccessToken,
  getToken,
  getValidToken,
  isTokenExpired,
  msUntilExpiry,
  saveUserData,
} from '../utils/auth';
import { setUnauthorizedHandler } from '../utils/authSession';
import { unregisterPush } from '../utils/push';

const AuthContext = createContext(null);

function userFromClaims(decoded, fallbacks = {}) {
  if (!decoded && !fallbacks.user_id) return null;
  return {
    sub: decoded?.sub ?? null,
    user_id: decoded?.user_id ?? fallbacks.user_id ?? null,
    user_type: decoded?.user_type ?? fallbacks.user_type ?? null,
  };
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const expiryTimerRef = useRef(null);

  const clearSessionState = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);
  }, []);

  const applySession = useCallback((token) => {
    const decoded = decodeAccessToken(token);
    if (!decoded || isTokenExpired(token, 0)) {
      clearUserData();
      clearSessionState();
      return false;
    }
    const nextUser = userFromClaims(decoded);
    setUser(nextUser);
    setUserType(decoded.user_type ?? null);
    setIsAuthenticated(true);

    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    const waitMs = msUntilExpiry(token);
    expiryTimerRef.current = setTimeout(() => {
      clearUserData();
      clearSessionState();
    }, waitMs);

    return true;
  }, [clearSessionState]);

  const logout = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      try {
        await unregisterPush(apiUrl, getToken);
      } catch (_) {}
    }
    clearUserData();
    clearSessionState();
  }, [clearSessionState]);

  const login = useCallback((token, nextUserType, userId) => {
    if (!token) {
      console.error('Login failed: No token provided');
      return;
    }
    saveUserData(token, nextUserType, userId);
    const decoded = decodeAccessToken(token);
    const nextUser = userFromClaims(decoded, {
      user_type: nextUserType,
      user_id: userId,
    });
    setUser(nextUser);
    setUserType(nextUserType ?? decoded?.user_type ?? null);
    setIsAuthenticated(true);

    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    const waitMs = msUntilExpiry(token);
    if (waitMs > 0) {
      expiryTimerRef.current = setTimeout(() => {
        clearUserData();
        clearSessionState();
      }, waitMs);
    }
  }, [clearSessionState]);

  useEffect(() => {
    const token = getValidToken();
    if (token) applySession(token);
    else clearSessionState();
  }, [applySession, clearSessionState]);

  useEffect(() => {
    setUnauthorizedHandler(() => logout());
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (isAuthenticated !== true) return;
      if (!getValidToken()) {
        clearSessionState();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated, clearSessionState]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== 'access_token') return;
      if (!event.newValue) {
        clearSessionState();
        return;
      }
      applySession(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [applySession, clearSessionState]);

  useEffect(() => () => {
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      userType,
      login,
      logout,
      isAdmin: () => userType === 'admin',
      isClinician: () => userType === 'clinician',
      isPatient: () => userType === 'patient',
    }),
    [isAuthenticated, user, userType, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
